/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { Typography2 } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { getActiveCoachModeId } from 'bundles/wes-common/components/WESAskCoursera/constants';

import _t from 'i18n!nls/wes-common';

import WESAskCourseraIcon from './WESAskCourseraIcon';

type Position = 'bottom-right' | 'bottom-left';
type ColorVariant = 'default' | 'inverted';

type WESAskCourseraLauncherProps = {
  isOpen: boolean;
  onClick: () => void;
  position?: Position;
  buttonText?: string;
  pillButtonText?: string;
  colorVariant?: ColorVariant;
  rotatingQuestions?: string[];
  onQuestionClick?: (question: string) => void;
};

// Animation keyframes
const slideUpFade = keyframes`
  0%   { opacity: 0; transform: translateY(8px); }
  10%  { opacity: 1; transform: translateY(0); }
  85%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-8px); }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pillGrowIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const styles = {
  container: (position: Position) => css`
    position: fixed;
    ${position === 'bottom-right' ? 'right: var(--cds-spacing-800);' : 'left: var(--cds-spacing-400);'}
    bottom: var(--cds-spacing-600);
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--cds-spacing-200);
    pointer-events: none;
  `,

  // Pill button that floats above the rectangular button
  pillWrapper: (isDismissing: boolean, colorVariant: ColorVariant) => css`
    display: flex;
    align-items: flex-start;
    position: absolute;
    bottom: 86px;
    right: 0;
    height: 100px;
    width: 300px;
    padding-left: 14px;
    padding-right: 14px;
    gap: 16px;
    background: var(--cds-color-neutral-background-primary);
    box-shadow: var(--cds-elevation-level2);
    border-radius: 16px;
    opacity: ${isDismissing ? 0 : 1};
    pointer-events: ${isDismissing ? 'none' : 'auto'};
    transition: opacity 0.3s ease-in-out, background-color 0.3s ease;

    &:hover {
      box-shadow: var(--cds-elevation-level2);
      background-color: ${colorVariant === 'inverted'
        ? 'none'
        : 'var(--cds-color-emphasis-primary-background-xweak, #F0F6FF)'};
      filter: ${colorVariant === 'inverted' ? 'brightness(0.95)' : 'none'};
    }
  `,

  // Wrapper for fade-out animation and close button
  pillButton: css`
    display: flex;
    align-items: flex-start;
    padding: 16px 0;
    margin: 0;
    border: none;
    background: none;
    font: inherit;
    text-align: left;
    box-sizing: border-box;
    cursor: pointer;
    white-space: wrap;
    pointer-events: auto;
    transition: all 0.2s ease;
    height: 100px;
    width: 252px;

    &:active {
      transform: scale(0.98);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }
  `,

  // Close button for dismissing the pill
  closeButtonWrapper: (colorVariant: ColorVariant) => css`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    padding: 16.99px 3.01px 0 0;
    border: none;
    background: none;
    font: inherit;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      filter: brightness(0.95);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }

    svg {
      width: 20px;
      height: 20px;
      color: ${colorVariant === 'inverted'
        ? 'var(--cds-color-white-0)'
        : 'var(--cds-color-neutral-foreground-primary)'};
    }
  `,

  // Main launcher button (circular with sparkle icon)
  launcherButton: (colorVariant: ColorVariant) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 178px;
    height: 70px;
    padding: 10px;
    border-radius: 16px;
    background-color: ${colorVariant === 'inverted'
      ? 'var(--cds-color-emphasis-primary-background-strong, #0056d2)'
      : 'var(--cds-color-white-0)'};
    border: ${colorVariant === 'inverted' ? 'none' : '0.63px solid var(--interactive-primary, #0056D2)'};
    box-shadow: var(--cds-elevation-level2);
    cursor: pointer;
    pointer-events: auto;
    transition: all 0.5s ease;

    &:hover {
      transform: scale(1.09);
      box-shadow: var(--cds-elevation-level2);
      background-color: ${colorVariant === 'inverted'
        ? 'none'
        : 'var(--cds-color-emphasis-primary-background-xweak, #F0F6FF)'};
      filter: ${colorVariant === 'inverted' ? 'brightness(0.95)' : 'none'};
    }

    &:focus-visible {
      outline: 3px solid var(--cds-color-white-0);
      outline-offset: 2px;
      border: 3px solid var(--cds-color-white-0);
      background-color: var(--cds-color-interactive-primary);
      box-shadow: 0 0 0 6px var(--cds-color-interactive-primary), var(--cds-elevation-level2);
    }

    &:active {
      transform: scale(0.98);
      filter: ${colorVariant === 'inverted' ? 'brightness(0.9)' : 'none'};
    }
  `,

  animatedText: (paused: boolean) => css`
    animation: ${slideUpFade} 5s ease-in-out both;
    animation-play-state: ${paused ? 'paused' : 'running'};
    transition: opacity 0.3s ease, transform 0.3s ease;
    ${paused &&
    `
  opacity: 1 !important;
  transform: translateY(0) !important;
`}
  `,

  launcherEntrance: css`
    animation: ${slideInFromRight} 0.5s ease-out;
  `,

  pillEntrance: css`
    animation: ${pillGrowIn} 0.2s ease-out;
    transform-origin: bottom right;
  `,

  iconWrapper: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  buttonText: css`
    padding-left: var(--cds-spacing-150);
  `,
};

const LAUNCHER_ENTRY_DELAY_MS = 5000; // Wait 5s after mount before showing launcher
const PILL_ENTRY_DELAY_MS = 500; // Wait 0.5s after launcher shows before showing pill
const PILL_DISMISS_ANIMATION_DURATION_MS = 300; // Wait 0.3s for dismissal animation to complete before hiding it

const PILL_STATES = {
  PILL_VISIBLE: 'PILL_VISIBLE',
  PILL_DISMISSED: 'PILL_DISMISSED',
  PILL_DISMISSING: 'PILL_DISMISSING',
} as const;

const PILL_ACTION = {
  SET_PILL_VISIBLE: 'SET_PILL_VISIBLE',
  SET_PILL_DISMISSED: 'SET_PILL_DISMISSED',
  SET_PILL_DISMISSING: 'SET_PILL_DISMISSING',
} as const;

function pillReducer(pillState: keyof typeof PILL_STATES, pillAction: keyof typeof PILL_ACTION) {
  switch (pillAction) {
    case PILL_ACTION.SET_PILL_VISIBLE:
      return PILL_STATES.PILL_VISIBLE;
    case PILL_ACTION.SET_PILL_DISMISSED:
      return PILL_STATES.PILL_DISMISSED;
    case PILL_ACTION.SET_PILL_DISMISSING:
      return PILL_STATES.PILL_DISMISSING;
    default:
      return pillState;
  }
}

const WESAskCourseraLauncher = ({
  isOpen,
  onClick,
  position = 'bottom-right',
  buttonText = _t('Ask Coursera'),
  pillButtonText = _t('Ask Coursera'),
  colorVariant = 'default',
  rotatingQuestions,
  onQuestionClick,
}: WESAskCourseraLauncherProps) => {
  const [pillState, pillAction] = useReducer(pillReducer, PILL_STATES.PILL_DISMISSED);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLauncherVisible, setIsLauncherVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const track = useTracker();
  const activeCoachModeId = getActiveCoachModeId();

  const hasRotatingQuestions = Boolean(rotatingQuestions?.length);

  useEffect(() => {
    let pillTimer: ReturnType<typeof setTimeout> | undefined;

    const launcherTimer = setTimeout(() => {
      setIsLauncherVisible(true);

      pillTimer = setTimeout(() => {
        pillAction(PILL_ACTION.SET_PILL_VISIBLE);
      }, PILL_ENTRY_DELAY_MS);
    }, LAUNCHER_ENTRY_DELAY_MS);

    return () => {
      clearTimeout(launcherTimer);
      if (pillTimer !== undefined) {
        clearTimeout(pillTimer);
      }
    };
  }, []);

  const handleAnimationEnd = useCallback<() => void>(() => {
    if (hasRotatingQuestions && rotatingQuestions) {
      setCurrentQuestionIndex((prev) => (prev + 1) % rotatingQuestions.length);
    }
  }, [hasRotatingQuestions, rotatingQuestions]);

  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleClose = useCallback<(e: React.SyntheticEvent) => void>(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      pillAction(PILL_ACTION.SET_PILL_DISMISSING);

      track('interact_coach', {
        coachInteractionType: 'close_coach_tool',
        coachAction: 'Dismiss Ask Coursera Pill',
        coach: {
          mode: activeCoachModeId,
        },
      });

      // Wait for animation to complete before hiding
      dismissTimeoutRef.current = setTimeout(() => {
        pillAction(PILL_ACTION.SET_PILL_DISMISSED);
      }, PILL_DISMISS_ANIMATION_DURATION_MS);
    },
    [track, activeCoachModeId]
  );

  useEffect(() => {
    return () => {
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
      }
    };
  }, []);

  const handlePillClick = () => {
    const currentQuestion = rotatingQuestions?.[currentQuestionIndex];
    if (currentQuestion && onQuestionClick) {
      onQuestionClick(currentQuestion);
    } else {
      onClick();
    }

    track('interact_coach', {
      coachInteractionType: 'launch_coach_tool',
      coachAction: 'Ask Coursera Pill',
      coach: {
        mode: activeCoachModeId,
      },
    });
  };

  if (!isLauncherVisible) {
    return null;
  }

  return (
    <div css={styles.container(position)} data-testid="WESAskCourseraLauncher">
      {/* Pill prompt - visible when chat is closed and not dismissed */}
      {pillState !== PILL_STATES.PILL_DISMISSED && (
        <div
          css={[
            styles.pillWrapper(pillState === PILL_STATES.PILL_DISMISSING, colorVariant),
            pillState !== PILL_STATES.PILL_DISMISSING && styles.pillEntrance,
          ]}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            type="button"
            onClick={handlePillClick}
            css={styles.pillButton}
            data-testid="WESAskCourseraLauncher-pill"
            aria-label={_t('Open AI Chatbot')}
          >
            <Typography2
              key={hasRotatingQuestions ? currentQuestionIndex : 'static'}
              variant="bodyPrimary"
              color={colorVariant === 'inverted' ? 'invertBody' : 'body'}
              component="p"
              css={hasRotatingQuestions ? styles.animatedText(isHovered) : undefined}
              onAnimationEnd={hasRotatingQuestions ? handleAnimationEnd : undefined}
            >
              {hasRotatingQuestions && rotatingQuestions?.[currentQuestionIndex]
                ? rotatingQuestions[currentQuestionIndex]
                : pillButtonText}
            </Typography2>
          </button>
          <button
            type="button"
            css={styles.closeButtonWrapper(colorVariant)}
            onClick={handleClose}
            aria-label={_t('Close')}
            data-testid="WESAskCourseraLauncher-close"
          >
            <CloseIcon size="small" />
          </button>
        </div>
      )}

      <button
        type="button"
        css={[styles.launcherButton(colorVariant), styles.launcherEntrance]}
        onClick={onClick}
        aria-label={isOpen ? _t('Close AI Chatbot') : _t('Open AI Chatbot')}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-controls="wes-chatbot-dialog"
        data-testid="WESAskCourseraLauncher-rectangular"
      >
        <div css={styles.iconWrapper}>
          <WESAskCourseraIcon size="large" colorVariant={colorVariant} />
          <Typography2 variant="subtitleMedium" color="emphasis.primary" component="p" css={styles.buttonText}>
            {buttonText}
          </Typography2>
        </div>
      </button>
    </div>
  );
};

export default WESAskCourseraLauncher;
